import { useEffect, useState } from "react";
import { apiClient, useAuth } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Check, Sparkles, Crown, Zap, Lock, Calendar as CalIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import BillingHistory from "@/components/BillingHistory";

const PLAN_META = {
  free: { icon: Zap, gradient: "from-slate-400 to-slate-500" },
  pro: { icon: Sparkles, gradient: "from-primary to-violet-500", featured: true },
  business: { icon: Crown, gradient: "from-amber-500 to-rose-500" },
};

function CurrentPlanCard({ user }) {
  if (!user) return null;
  const plan = user.plan || "free";
  const created = user.created_at ? new Date(user.created_at) : new Date();
  const renew = new Date(created);
  renew.setMonth(renew.getMonth() + 1);
  while (renew < new Date()) renew.setMonth(renew.getMonth() + 1);
  const meta = PLAN_META[plan];
  const Icon = meta.icon;
  return (
    <div className="border border-border rounded-2xl p-6 bg-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-white`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Plan actuel</p>
            <h2 className="text-xl font-black tracking-tight font-display capitalize">Local Pulse · {plan}</h2>
            {plan !== "free" ? (
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <CalIcon className="h-3 w-3" />Prochain prélèvement le {renew.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">Aucun engagement · sans carte bancaire</p>
            )}
          </div>
        </div>
        {plan !== "free" && (
          <Button variant="outline" className="rounded-full" data-testid="manage-billing-btn" onClick={() => toast.info("Le portail Stripe ouvrira en production.")}>
            Gérer l'abonnement<ExternalLink className="h-3.5 w-3.5 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState("");
  const [cycle, setCycle] = useState("annual");

  useEffect(() => { apiClient.get("/plans").then(r => setPlans(r.data)); }, []);

  const subscribe = async (id) => {
    if (id === "free") return;
    setLoading(id);
    try {
      const r = await apiClient.post("/checkout", { plan_id: id, billing_cycle: cycle, origin_url: window.location.origin });
      if (!r?.data?.url) {
        toast.error("Stripe n'a pas renvoyé d'URL de paiement. Veuillez réessayer.");
        setLoading("");
        return;
      }
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Surface the *real* error so the user knows what to fix (env var, KYC, etc.)
      const msg = detail
        ? (typeof detail === "string" ? detail : JSON.stringify(detail))
        : (err?.message || "Erreur de connexion à Stripe");
      // eslint-disable-next-line no-console
      console.error("[Checkout]", { status, detail, err });
      toast.error(msg, { duration: 8000, description: status ? `Code ${status}` : undefined });
      setLoading("");
    }
  };

  return (
    <div className="space-y-8" data-testid="pricing-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Abonnement</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
          Choisissez votre <span className="font-serif-italic font-normal">cadence de croissance</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          Sans engagement · annulable en un clic · 7 jours d'essai gratuit.
        </p>
      </div>

      <CurrentPlanCard user={user} />

      <div className="flex items-center justify-center">
        <div className="inline-flex items-center p-1 bg-muted/60 rounded-full border border-border" data-testid="billing-toggle">
          <button
            onClick={() => setCycle("monthly")}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${cycle === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
            data-testid="cycle-monthly">
            Mensuel
          </button>
          <button
            onClick={() => setCycle("annual")}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${cycle === "annual" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
            data-testid="cycle-annual">
            Annuel
            <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">−20%</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(plans).map(([id, p]) => {
          const meta = PLAN_META[id] || PLAN_META.free;
          const Icon = meta.icon;
          const featured = meta.featured;
          const current = user?.plan === id;
          const monthly = p.price_monthly || 0;
          const annual = p.price_annual || 0;
          const displayPrice = cycle === "annual" ? Math.round(annual / 12) : monthly;
          const fullPrice = cycle === "annual" ? annual : monthly;

          return (
            <div key={id}
              className={`relative rounded-2xl p-7 transition-all ${
                featured
                  ? "bg-gradient-to-br from-primary/5 to-violet-500/5 border-2 border-primary/40 ring-premium"
                  : "bg-card border border-border card-hover"
              }`}
              data-testid={`plan-${id}`}>
              {featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-violet-500 text-white text-[10px] font-bold uppercase tracking-wider">
                  Le plus choisi
                </div>
              )}
              {current && (
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-bold uppercase tracking-wider">
                  Actuel
                </div>
              )}
              <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-white mb-4`}>
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="text-2xl font-black tracking-tight">{p.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>

              <div className="mt-6 min-h-[80px]">
                <div className="flex items-baseline gap-1">
                  <span className={`text-5xl font-black font-display ${featured ? "text-gradient" : ""}`}>{displayPrice}€</span>
                  <span className="text-sm text-muted-foreground">/mois</span>
                </div>
                {id !== "free" && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {cycle === "annual" ? (
                      <>Facturé <span className="font-semibold">{fullPrice}€/an</span> · économisez {Math.round(monthly * 12 - annual)}€</>
                    ) : (
                      <>Facturé chaque mois · annulable à tout moment</>
                    )}
                  </p>
                )}
                {id === "free" && <p className="text-xs text-muted-foreground mt-1.5">Gratuit à vie · sans carte</p>}
              </div>

              <ul className="mt-5 space-y-2.5 text-sm">
                {p.features?.map(f => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span className="mt-0.5 h-4 w-4 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                      <Check className="h-2.5 w-2.5 text-primary" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Button disabled={loading === id || current || id === "free"} onClick={() => subscribe(id)}
                className={`w-full mt-7 rounded-full h-11 ${featured ? "btn-glow" : ""}`}
                variant={featured ? "default" : "outline"} data-testid={`subscribe-${id}`}>
                {current ? "Plan actuel" : id === "free" ? "Plan par défaut" : loading === id ? "Redirection vers Stripe…" : `Passer ${p.name}`}
              </Button>
              {id !== "free" && !current && (
                <p className="text-xs text-center text-muted-foreground mt-2 flex items-center justify-center gap-1.5">
                  <Lock className="h-3 w-3" />Paiement sécurisé · annulation 1 clic
                </p>
              )}
            </div>
          );
        })}
      </div>

      <BillingHistory />

      <div className="border border-border rounded-2xl p-5 bg-card flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Check className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <p className="text-sm font-semibold">Essai gratuit · 7 jours</p>
            <p className="text-xs text-muted-foreground">Annulez en un clic. Aucun frais caché. Vos données restent les vôtres.</p>
          </div>
        </div>
        <div className="text-xs text-muted-foreground hidden sm:block">
          Paiement sécurisé via <span className="font-semibold text-foreground">Stripe</span> · Hébergement européen · RGPD
        </div>
      </div>
    </div>
  );
}
